import { ref, computed, onMounted } from 'vue';

import { useEventListener } from '@composables';

import type { CalendarState } from './calendar-state';

/**
 * Creates a set of parameters that correspond to various display-based aspects
 * of a calendar day.
 *
 * @param calendar The calendar state.
 * @returns A set of parameters that correspond to various display-based aspects
 * of a calendar day.
 */
export function useCalendarDayParams({
  itemTop,
  itemContentHeight,
  itemBorderHeight,
}: CalendarState) {
  const itemHeight = computed(() => {
    return toPixels(itemContentHeight.value) + toPixels(itemBorderHeight.value);
  });

  // Height of the `CalendarDay` component.
  const dayHeight = ref(0);

  // Offset from the top of the `CalendarDay` component at which the first
  // calendar item is displayed.
  const topOffset = computed(() => {
    return toPixels(itemTop.value);
  });

  // Offset from the bottom of the `CalendarDay` component that represents the
  // the buffer space between the last calendar item that can be displayed and
  // the "footer" of the `CalendarDay` component.
  const bottomOffset = 36;

  // Maximum number of calendar items that can be displayed in the `CalendarDay`
  // component.
  const maxDisplayable = computed(() => {
    const count =
      (dayHeight.value - topOffset.value - bottomOffset) / itemHeight.value;

    return Math.max(0, Math.floor(count));
  });

  const updateDayHeight = () => {
    dayHeight.value =
      document.getElementsByClassName('calendar-day')[0]?.clientHeight ?? 0;
  };

  onMounted(updateDayHeight);
  useEventListener('resize', updateDayHeight);

  return {
    itemHeight,
    dayHeight,
    topOffset,
    bottomOffset,
    maxDisplayable,
  };
}

export type CalendarDayParams = ReturnType<typeof useCalendarDayParams>;

//#region Helper Functions

function toPixels(size: string) {
  const element = document.createElement('div');
  element.style.position = 'absolute';
  element.style.visibility = 'hidden';
  element.style.height = size;
  document.body.appendChild(element);

  const pixels = element.offsetHeight;
  document.body.removeChild(element);

  return pixels;
}
//#endregion Helper Functions
